import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type MessageAuthor, type MessageReaction, type Uuid } from "@shared/api";
import { Dialog } from "@shared/ui/bits";
import { Spinner } from "@shared/ui/spinner";
import { fromAuthor, Tile } from "@entities/user";

/** Кто поставил значок — общий ключ у подсказки на реакции и окна «Реакции». */
export function useReactors(channelId: Uuid, messageId: Uuid, emoji: string, enabled = true) {
  return useQuery({
    queryKey: ["reactors", messageId, emoji],
    queryFn: () => api.messages.reactors(channelId, messageId, emoji),
    enabled,
    staleTime: 15_000,
  });
}

/** Подпись подсказки: «Аня, Боря и ещё 3». */
export function reactorsLine(people: MessageAuthor[] | undefined, count: number): string {
  if (!people) return "…";
  const names = people.slice(0, 3).map((person) => person.display_name || person.username);
  const rest = count - names.length;
  return rest > 0 ? `${names.join(", ")} и ещё ${rest}` : names.join(", ");
}

/**
 * «Реакции» — как в Discord: слева значки со счётом, справа — кто поставил
 * выбранный, в порядке, в каком ставили.
 */
export function ReactionsDialog({
  channelId,
  messageId,
  reactions,
  mark,
  onClose,
}: {
  channelId: Uuid;
  messageId: Uuid;
  reactions: MessageReaction[];
  /** Как нарисовать значок — свой знак сервера картинкой. */
  mark: (emoji: string) => React.ReactNode;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState(reactions[0]?.emoji ?? "");
  const { data: people, isPending } = useReactors(channelId, messageId, picked, !!picked);

  return (
    <Dialog title="Реакции" onClose={onClose}>
      <div className="reactions-dialog">
        <div className="reactions-dialog-tabs" role="tablist" aria-label="Значки">
          {reactions.map((reaction) => (
            <button
              key={reaction.emoji}
              type="button"
              role="tab"
              aria-selected={picked === reaction.emoji}
              className={`reactions-dialog-tab ${picked === reaction.emoji ? "on" : ""}`}
              onClick={() => setPicked(reaction.emoji)}
            >
              <span className="reaction-emoji">{mark(reaction.emoji)}</span>
              <span className="reaction-count">{reaction.count}</span>
            </button>
          ))}
        </div>
        <div className="reactions-dialog-people">
          {isPending ? (
            <Spinner />
          ) : (
            (people ?? []).map((person) => (
              <div key={person.id} className="reactions-dialog-person">
                <Tile person={fromAuthor(person)} size={26} mark={null} />
                <span className="ellipsis">{person.display_name || person.username}</span>
                <span className="tiny muted ellipsis">{person.handle}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </Dialog>
  );
}
